import { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { UserProfile, UserPreferences, CharacterClass } from '../types/user';
import { signalRService } from '../services/signalr/SignalRService';
import { apiService } from '../services/api/apiService';
import { HubMethods, HubEvents, UserProfile as SignalRUserProfile, RegisterUserRequest, AuthResponse } from '../services/signalr/types';
import { unityPreloader } from '../services/unityPreloader';
import { refreshSession } from '../services/api/authApi';
import { emit as emitTelemetry, newCorrelationId, nowMs } from '../services/telemetry/FlexoTelemetry';

// Registration payload: a new profile plus the password chosen in the signup
// form (ProfileSetup). The password only travels to the server on this call —
// it is never stored on the in-memory UserProfile.
export type RegistrationProfile = Omit<UserProfile, 'id' | 'createdAt' | 'lastActive' | 'isOnline'> & {
  password?: string;
};

interface UserContextType {
  user: UserProfile | null;
  isLoggedIn: boolean;
  isAuthReady: boolean; // True when auth token is verified/ready for API calls
  isLoading: boolean;
  isConnected: boolean;
  connectionError: string | null;
  authError: string | null;
  loginWithCredentials: (email: string, password: string, rememberMe?: boolean) => Promise<boolean>;
  login: (profile: UserProfile) => Promise<void>;
  register: (profile: RegistrationProfile) => Promise<boolean>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  updatePreferences: (preferences: UserPreferences) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const STORAGE_KEY = 'gamedate_user';
const TOKEN_KEY = 'gamedate_token';

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false); // True when token is verified
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  // Load cached user from localStorage and connect to SignalR
  useEffect(() => {
    const initializeUser = async () => {
      // First load from localStorage for quick startup
      const stored = localStorage.getItem(STORAGE_KEY);
      let cachedUser: UserProfile | null = null;

      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          parsed.createdAt = new Date(parsed.createdAt);
          parsed.lastActive = new Date(parsed.lastActive);
          cachedUser = parsed;
          setUser(parsed);

          // Restore API token
          const storedToken = localStorage.getItem(TOKEN_KEY);
          if (storedToken) {
            apiService.setToken(storedToken);
          }
        } catch (e) {
          console.error('Failed to parse stored user:', e);
        }
      }

      // V3 A0-6: get a valid token BEFORE opening the realtime connection.
      //
      // This used to be the other way round — connect anonymously, then invoke
      // ReconnectUser(userId) over that anonymous connection to mint a token. That hub method
      // checked no credential at all, so it handed an admin JWT to anyone who asked for "admin".
      // It is now authenticated, and SignalR binds the principal at connect time, so the token
      // has to exist first. `POST /auth/refresh` exchanges the stored token (expired is fine,
      // forged is not) for a fresh one.
      let activeToken: string | null = null;
      if (cachedUser) {
        const storedToken = localStorage.getItem(TOKEN_KEY);
        const refreshCid = newCorrelationId();
        emitTelemetry({ event: 'session_refresh_attempted', roomId: null, playerId: cachedUser.id, correlationId: refreshCid, ts: nowMs() });
        const refreshed = storedToken ? await refreshSession(storedToken) : null;
        if (refreshed) {
          activeToken = refreshed.token;
          apiService.setToken(refreshed.token);
          localStorage.setItem(TOKEN_KEY, refreshed.token);
          setIsAuthReady(true);
          emitTelemetry({ event: 'session_refresh_succeeded', roomId: null, playerId: cachedUser.id, correlationId: refreshCid, ts: nowMs() });
        } else {
          // No usable session: land on the login screen rather than on a half-authenticated app.
          console.warn('[UserContext] Session refresh failed, clearing auth');
          cachedUser = null;
          setUser(null);
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(TOKEN_KEY);
          apiService.setToken(null);
          emitTelemetry({
            event: 'session_refresh_failed', roomId: null, playerId: null, correlationId: refreshCid, ts: nowMs(),
            payload: { reason: 'invalid' },
          });
        }
      }

      // Connect to SignalR
      try {
        signalRService.setEventHandlers({
          onConnectionStateChange: (state) => {
            setIsConnected(state === 'connected');
            if (state === 'error') {
              setConnectionError('Connection failed');
            } else {
              setConnectionError(null);
            }
          },
          onError: (error) => {
            setConnectionError(error.message);
          },
        });

        // The connection carries the token from here on: every hub method except Login and
        // RegisterUser refuses an anonymous caller (V3 A0-6). Group healing / session restore is
        // done by SignalRService.connect, which invokes ReconnectUser once the token is attached.
        await signalRService.connect(cachedUser?.id, activeToken ?? undefined);
      } catch (e) {
        console.error('SignalR connection failed:', e);
        setConnectionError('Failed to connect to server');
      }

      setIsLoading(false);

      // Start preloading Unity assets in background if user is logged in
      if (cachedUser) {
        unityPreloader.startPreloading().catch(console.error);
      }
    };

    initializeUser();

    // Register the apiService 401 interceptor's silent-refresh + auth-cleared hooks.
    // Refresh re-runs ReconnectUser (which now re-applies the ban/suspend gate server-side).
    apiService.setRefreshHandler(async () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      let uid: string | null = null;
      try { uid = JSON.parse(stored).id; } catch { return null; }
      if (!uid) return null;

      const cid = newCorrelationId();
      emitTelemetry({ event: 'session_refresh_attempted', roomId: null, playerId: uid, correlationId: cid, ts: nowMs() });
      try {
        // V3 A0-6: refresh over REST, not over the hub. The hub path required no credential.
        const stale = localStorage.getItem(TOKEN_KEY);
        const resp = stale ? await refreshSession(stale) : null;
        if (resp?.token) {
          localStorage.setItem(TOKEN_KEY, resp.token);
          emitTelemetry({ event: 'session_refresh_succeeded', roomId: null, playerId: uid, correlationId: cid, ts: nowMs() });
          return resp.token;
        }
        emitTelemetry({
          event: 'session_refresh_failed', roomId: null, playerId: uid, correlationId: cid, ts: nowMs(),
          payload: { reason: 'invalid' },
        });
        return null;
      } catch (e) {
        console.warn('[UserContext] Silent refresh failed:', e);
        emitTelemetry({
          event: 'session_refresh_failed', roomId: null, playerId: uid, correlationId: cid, ts: nowMs(),
          payload: { reason: 'error' },
        });
        return null;
      }
    });

    apiService.setOnAuthCleared(() => {
      setUser(null);
      setIsAuthReady(false);
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_KEY);
      apiService.setToken(null);
    });

    // Set up event listeners
    const unsubUserRegistered = signalRService.on<[SignalRUserProfile]>(
      HubEvents.UserRegistered,
      (signalRUser) => {
        const newUser = mapSignalRUserToProfile(signalRUser, user?.preferences);
        setUser(newUser);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser));
      }
    );

    const unsubProfileUpdated = signalRService.on<[SignalRUserProfile]>(
      HubEvents.ProfileUpdated,
      (signalRUser) => {
        setUser((prev) => {
          if (prev && prev.id === signalRUser.id) {
            const updated = mapSignalRUserToProfile(signalRUser, prev.preferences, prev);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            return updated;
          }
          return prev;
        });
      }
    );

    return () => {
      unsubUserRegistered();
      unsubProfileUpdated();
      apiService.setRefreshHandler(null);
      apiService.setOnAuthCleared(null);
    };
  }, []);

  // Save user to localStorage on change
  useEffect(() => {
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [user]);

  const loginWithCredentials = useCallback(async (email: string, password: string, rememberMe = false): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);

    const correlationId = newCorrelationId();
    emitTelemetry({ event: 'login_attempted', roomId: null, playerId: null, correlationId, ts: nowMs() });

    try {
      // Try to login via SignalR
      const response = await signalRService.invoke<AuthResponse>(
        HubMethods.Login,
        { email, password, rememberMe }
      );

      if (response.success && response.user) {
        const loggedInUser = mapSignalRUserToProfile(response.user);
        setUser(loggedInUser);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(loggedInUser));

        // Set API token for authenticated requests
        if (response.token) {
          apiService.setToken(response.token);
          localStorage.setItem(TOKEN_KEY, response.token);
          // Store remember me preference for session persistence
          if (rememberMe) {
            localStorage.setItem('gamedate_session_persist', 'true');
          } else {
            localStorage.removeItem('gamedate_session_persist');
          }
          setIsAuthReady(true); // Token is valid

          // V3 A0-6: Login ran over an ANONYMOUS connection (it is one of the two hub methods
          // that may). SignalR binds the principal at connect time, so that connection stays
          // anonymous no matter how successful the login was — and every other hub method now
          // refuses an anonymous caller. Reopen it with the token before anything else runs.
          try {
            await signalRService.reconnectWithToken(loggedInUser.id, response.token);
          } catch (e) {
            console.error('[UserContext] Failed to rebind the realtime connection after login:', e);
            setConnectionError('Signed in, but the realtime connection failed. Please reload.');
          }
        }

        emitTelemetry({ event: 'login_succeeded', roomId: null, playerId: loggedInUser.id, correlationId, ts: nowMs() });

        // Start preloading Unity assets in background
        unityPreloader.startPreloading().catch(console.error);

        return true;
      } else {
        // Pass the server's specific message through so banned/suspended text survives;
        // only fall back to a generic string when the server sent nothing. A server/transport
        // fault ('error') gets a retry-oriented message, not the misleading "Invalid email".
        setAuthError(
          response.reason === 'error'
            ? (response.error || 'Service issue — please try again in a moment.')
            : (response.error || 'Invalid email or password')
        );
        emitTelemetry({
          event: 'login_rejected', roomId: null, playerId: null, correlationId, ts: nowMs(),
          payload: { reason: response.reason || 'invalid' },
        });
        return false;
      }
    } catch (e) {
      console.error('Login failed:', e);
      setAuthError('Login failed. Please try again.');
      emitTelemetry({
        // Client-side throw = transport/server fault, not bad credentials. Emit 'error' so the
        // analytics ring buffer can distinguish the two (matches the refresh-handler catch).
        event: 'login_rejected', roomId: null, playerId: null, correlationId, ts: nowMs(),
        payload: { reason: 'error' },
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (profile: UserProfile) => {
    const userWithStatus = { ...profile, lastActive: new Date(), isOnline: true };
    setUser(userWithStatus);

    // Register/update with SignalR
    if (signalRService.isConnected) {
      try {
        const response = await signalRService.invoke<AuthResponse>(HubMethods.ReconnectUser, profile.id);

        // Set API token if returned
        if (response.token) {
          apiService.setToken(response.token);
          localStorage.setItem(TOKEN_KEY, response.token);
          setIsAuthReady(true);
        }

        // Send proper UserPresence object (backend expects Status as enum: 0=Online, 1=Away, 2=Busy, 3=Offline)
        await signalRService.invoke(HubMethods.UpdatePresence, {
          userId: profile.id,
          username: profile.displayName,
          status: 0, // Online
          lastSeen: new Date().toISOString(),
        });
      } catch (e) {
        console.error('Failed to register with SignalR:', e);
      }
    }
  }, []);

  const register = useCallback(async (profile: RegistrationProfile): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);

    try {
      // Create registration request - include password for DB persistence
      const request: RegisterUserRequest = {
        displayName: profile.displayName,
        email: profile.email,
        password: profile.password,
        avatar: profile.avatar,
        bio: profile.bio,
        age: profile.age,
        gender: profile.gender,
        characterClass: profile.characterClass,
        interests: profile.interests,
      };

      // Register with SignalR server
      const response = await signalRService.invoke<{ success: boolean; user: SignalRUserProfile; token?: string; error?: string }>(
        HubMethods.RegisterUser,
        request
      );

      if (response.success && response.user) {
        const newUser = mapSignalRUserToProfile(response.user, profile.preferences, profile as Partial<UserProfile>);
        setUser(newUser);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newUser));

        // Set API token if returned
        if (response.token) {
          apiService.setToken(response.token);
          localStorage.setItem(TOKEN_KEY, response.token);
          setIsAuthReady(true);
          // Same rebind as login: RegisterUser also runs on the anonymous bootstrap connection.
          try {
            await signalRService.reconnectWithToken(newUser.id, response.token);
          } catch (e) {
            console.error('[UserContext] Failed to rebind the realtime connection after registration:', e);
          }
        }

        // Start preloading Unity assets in background
        unityPreloader.startPreloading().catch(console.error);
        return true;
      } else {
        throw new Error(response.error || 'Registration failed');
      }
    } catch (e) {
      console.error('Registration failed:', e);
      setAuthError(e instanceof Error ? e.message : 'Registration failed. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    // Notify server
    if (signalRService.isConnected && user) {
      try {
        // Send proper UserPresence object (backend expects Status as enum: 0=Online, 1=Away, 2=Busy, 3=Offline)
        await signalRService.invoke(HubMethods.UpdatePresence, {
          userId: user.id,
          username: user.displayName,
          status: 3, // Offline
          lastSeen: new Date().toISOString(),
        });
      } catch (e) {
        console.error('Failed to update presence:', e);
      }
    }

    // V3 A0-3: end the session server-side so the bearer token is dead immediately, not at its
    // natural expiry. Best-effort — a failed logout must never trap the user in a signed-in UI.
    try {
      await apiService.post('/auth/logout');
    } catch (e) {
      console.warn('[UserContext] Server-side logout failed:', e);
    }
    try {
      await signalRService.disconnect();
    } catch (e) {
      console.warn('[UserContext] Failed to close the realtime connection on logout:', e);
    }

    // Stop preloading Unity assets
    unityPreloader.stopPreloading();

    setUser(null);
    setAuthError(null);
    setIsAuthReady(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    apiService.setToken(null);
  }, [user]);

  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    const updatedUser = user ? { ...user, ...updates, lastActive: new Date() } : null;

    if (updatedUser) {
      setUser(updatedUser);

      // Sync with server
      if (signalRService.isConnected) {
        try {
          await signalRService.invoke(HubMethods.UpdateProfile, {
            displayName: updatedUser.displayName,
            avatar: updatedUser.avatar,
            bio: updatedUser.bio,
            age: updatedUser.age,
            gender: updatedUser.gender,
            characterClass: updatedUser.characterClass,
            interests: updatedUser.interests,
          });
        } catch (e) {
          console.error('Failed to update profile on server:', e);
        }
      }
    }
  }, [user]);

  const updatePreferences = useCallback((preferences: UserPreferences) => {
    setUser(prev => prev ? { ...prev, preferences, lastActive: new Date() } : null);
    // Preferences are stored locally only for now
  }, []);

  return (
    <UserContext.Provider value={{
      user,
      isLoggedIn: !!user,
      isAuthReady,
      isLoading,
      isConnected,
      connectionError,
      authError,
      loginWithCredentials,
      login,
      register,
      logout,
      updateProfile,
      updatePreferences,
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

// Helper functions
function mapSignalRUserToProfile(
  signalRUser: SignalRUserProfile,
  existingPreferences?: UserPreferences,
  existingProfile?: Partial<UserProfile>
): UserProfile {
  const gender = signalRUser.gender === 'female' ? 'female' : 'male';
  const characterClass = (signalRUser.characterClass as CharacterClass) || existingProfile?.characterClass || 'tank';
  return {
    id: signalRUser.id,
    username: signalRUser.displayName.toLowerCase().replace(/\s+/g, '_'),
    displayName: signalRUser.displayName,
    email: signalRUser.email || '',
    avatar: signalRUser.avatar,
    bio: signalRUser.bio || '',
    age: signalRUser.age || 18,
    gender,
    characterClass,
    lookingFor: existingProfile?.lookingFor || ['male', 'female'],
    interests: signalRUser.interests || [],
    preferences: existingPreferences || {
      minAge: 18,
      maxAge: 99,
      interests: [],
      showOnlineOnly: false,
    },
    createdAt: new Date(signalRUser.createdAt),
    lastActive: new Date(signalRUser.lastActive),
    isOnline: true,
  };
}
